(() => {
  const KEY = 'nbdc:wishlist';

  const read = () => {
    try {
      const list = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  };

  const write = (list) => {
    localStorage.setItem(KEY, JSON.stringify(list));
    document.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { list } }));
  };

  const has = (handle) => read().includes(handle);

  const toggle = (handle) => {
    const list = read();
    const index = list.indexOf(handle);
    index > -1 ? list.splice(index, 1) : list.push(handle);
    write(list);
  };

  const formatMoney = (cents) => {
    return (cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€';
  };

  class WishlistToggle extends HTMLElement {
    connectedCallback() {
      this.handle = this.getAttribute('handle');
      this.button = this.querySelector('button');
      this.button?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        toggle(this.handle);
      });
      this._listener = () => this.update();
      document.addEventListener('wishlist:changed', this._listener);
      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener('wishlist:changed', this._listener);
    }

    update() {
      const active = has(this.handle);
      this.classList.toggle('is-active', active);
      this.button?.setAttribute('aria-pressed', active ? 'true' : 'false');
    }
  }

  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this._listener = () => this.update();
      document.addEventListener('wishlist:changed', this._listener);
      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener('wishlist:changed', this._listener);
    }

    update() {
      const count = read().length;
      this.textContent = count;
      this.classList.toggle('opacity-0', count === 0);
    }
  }

  class WishlistItems extends HTMLElement {
    connectedCallback() {
      this._listener = () => this.render();
      document.addEventListener('wishlist:changed', this._listener);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('wishlist:changed', this._listener);
    }

    async render() {
      const list = read();
      const drawer = this.closest('#wishlist-drawer');
      const empty = drawer?.querySelector('.wishlist-drawer__empty');

      if (empty) {
        empty.toggleAttribute('hidden', list.length > 0);
      }

      if (!list.length) {
        this.innerHTML = '';
        return;
      }

      const responses = await Promise.all(
        list.map((handle) =>
          fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`)
            .then((response) => (response.ok ? response.json() : null))
            .catch(() => null)
        )
      );

      this.innerHTML = responses
        .filter(Boolean)
        .map((product) => {
          const image = product.featured_image
            ? `${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=180`
            : '';
          const comparePrice = product.compare_at_price > product.price
            ? `<s class="wishlist-item__compare">${formatMoney(product.compare_at_price)}</s>`
            : '';

          return `
            <div class="wishlist-item">
              <a href="${product.url}" class="wishlist-item__figure">
                ${image ? `<img src="${image}" alt="" loading="lazy" width="90" height="112">` : ''}
              </a>
              <div class="wishlist-item__info">
                <a href="${product.url}" class="wishlist-item__title">${product.title}</a>
                <div class="wishlist-item__price">
                  <span>${formatMoney(product.price)}</span>
                  ${comparePrice}
                </div>
              </div>
              <button type="button" class="wishlist-item__remove" data-wishlist-remove="${product.handle}" aria-label="Remove">
                <svg role="presentation" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><path d="m3 3 10 10M13 3 3 13"></path></svg>
              </button>
            </div>`;
        })
        .join('');

      this.querySelectorAll('[data-wishlist-remove]').forEach((button) => {
        button.addEventListener('click', () => toggle(button.dataset.wishlistRemove));
      });
    }
  }

  if (!customElements.get('wishlist-toggle')) customElements.define('wishlist-toggle', WishlistToggle);
  if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);
  if (!customElements.get('wishlist-items')) customElements.define('wishlist-items', WishlistItems);
})();
